/*
 * Mobile sticky add-to-cart bar. Hidden on load; shown only after the main
 * add-to-cart button has been in the viewport at least once and then left it
 * through the top edge (the shopper scrolled past it). Scrolling back up so the
 * button leaves through the bottom edge hides it again. Clicking it clicks the main button, so
 * Dawn's product-form handles the add. Variant changes are still handled by
 * Dawn's product-info.js — this only mirrors the freshly rendered section HTML
 * (published on PUB_SUB_EVENTS.variantChange) into the bar and the H1, which
 * product-info.js does not update.
 */
if (!customElements.get('pdp-sticky-atc')) {
  customElements.define(
    'pdp-sticky-atc',
    class PdpStickyAtc extends HTMLElement {
      connectedCallback() {
        this.sectionId = this.dataset.section;
        this.mainButton = document.getElementById(`ProductSubmitButton-${this.sectionId}`);

        this.addEventListener('click', (event) => {
          if (!event.target.closest('[data-pdp-sticky-button]')) return;
          this.mainButton?.click();
        });

        if (this.mainButton && 'IntersectionObserver' in window) {
          // The observer only answers "has the shopper seen the main button yet?". From then on
          // a passive, rAF-throttled scroll check decides visibility from the button's position,
          // because the observer stays silent when the button jumps across the viewport without
          // intersecting it (e.g. a jump to the top of the page or to an anchor).
          this.observer = new IntersectionObserver(([entry]) => {
            if (entry.isIntersecting) this.startTracking();
            this.update();
          });
          this.observer.observe(this.mainButton);
        }

        // Publish the bar's real height (safe-area padding included) as --sticky-atc-h on <html>;
        // pdp.css lifts the Shopify Inbox chat button above the bar with it.
        if ('ResizeObserver' in window) {
          this.resizeObserver = new ResizeObserver(([entry]) => {
            const height = entry.borderBoxSize?.[0]?.blockSize ?? this.offsetHeight;
            document.documentElement.style.setProperty('--sticky-atc-h', `${height}px`);
          });
          this.resizeObserver.observe(this, { box: 'border-box' });
        }

        // PUB_SUB_EVENTS is a top-level `const` in Dawn's constants.js: global, but not on `window`.
        if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
          this.unsubscribe = subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange.bind(this));
        }
      }

      disconnectedCallback() {
        this.observer?.disconnect();
        this.resizeObserver?.disconnect();
        this.unsubscribe?.();
        if (this.onScroll) window.removeEventListener('scroll', this.onScroll);
      }

      startTracking() {
        if (this.onScroll) return;
        this.onScroll = () => {
          if (this.scrollFrame) return;
          this.scrollFrame = requestAnimationFrame(() => {
            this.scrollFrame = null;
            this.update();
          });
        };
        window.addEventListener('scroll', this.onScroll, { passive: true });
      }

      update() {
        const scrolledPast = !!this.onScroll && this.mainButton.getBoundingClientRect().bottom <= 0;
        this.toggle(scrolledPast);
      }

      toggle(visible) {
        if (this.classList.contains('is-visible') === visible) return;
        this.classList.toggle('is-visible', visible);
        this.setAttribute('aria-hidden', String(!visible));
        this.toggleAttribute('inert', !visible);
      }

      onVariantChange({ data: { sectionId, html, variant } }) {
        if (sectionId !== this.sectionId) return;
        // In-page variant switch (assets/product-info.js renderLocalVariant): no HTML, the data is
        // enough - the H1 never changes there, only the bar's variant, price and button.
        if (!html) {
          if (variant) this.renderVariant(variant);
          return;
        }
        [`PdpSticky-${sectionId}`, `PdpTitle-${sectionId}`].forEach((id) => {
          const source = html.getElementById(id);
          const destination = document.getElementById(id);
          if (source && destination) destination.innerHTML = source.innerHTML;
        });
      }

      renderVariant(variant) {
        const title = this.querySelector('.pdp-sticky__variant');
        const price = this.querySelector('.pdp-sticky__price');
        const button = this.querySelector('[data-pdp-sticky-button]');
        if (title) title.textContent = variant.title;
        if (price && variant.sticky_price) price.textContent = variant.sticky_price;
        if (button) {
          button.disabled = !variant.available;
          const strings = window.variantStrings || {};
          const text = variant.available ? strings.addToCart : strings.soldOut;
          if (text) button.textContent = text;
        }
      }
    }
  );
}
